import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import VideoUploader from "@/components/feature/VideoUploader";
import ImageUploader from "@/components/feature/ImageUploader";
import VideoCard from "@/components/feature/VideoCard";
import VideoPlayerModal from "@/components/feature/VideoPlayerModal";
import { deleteMediaByUrl, isOwnMedia } from "@/services/mediaUpload";
import {
  createCommunityVideo,
  createModule,
  deleteCommunityVideo,
  deleteModule,
  ensureDefaultModules,
  fetchCommunityVideos,
  fetchModules,
  patchCommunityVideo,
  saveCommunityVideo,
  updateModule,
  type CommunityModule,
  type CommunityVideo,
  type VideoInput,
  type VideoLayout,
} from "@/services/communityVideos";

const inputCls =
  "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";

const ICONS: { value: string; label: string }[] = [
  { value: "ri-plant-line", label: "Crecimiento" },
  { value: "ri-money-dollar-circle-line", label: "Finanzas" },
  { value: "ri-mic-line", label: "Oratoria" },
  { value: "ri-rocket-line", label: "Negocio" },
  { value: "ri-robot-2-line", label: "IA" },
  { value: "ri-group-line", label: "Liderazgo" },
  { value: "ri-graduation-cap-line", label: "Aprendizaje" },
  { value: "ri-heart-3-line", label: "Bienestar" },
  { value: "ri-lightbulb-line", label: "Ideas" },
  { value: "ri-trophy-line", label: "Logros" },
  { value: "ri-movie-2-line", label: "Video" },
  { value: "ri-star-line", label: "Estrella" },
];

const emptyVideo = (moduleId: string): VideoInput => ({
  moduleId,
  title: "",
  description: "",
  videoUrl: "",
  coverLandscapeUrl: "",
  coverPortraitUrl: "",
  layout: "landscape",
  featured: false,
  durationSeconds: null,
  published: true,
});

function fmtDuration(s: number | null): string {
  if (!s) return "";
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")} min`;
}

export default function CourseVideos({ onToast: onToastProp }: { onToast: (msg: string) => void }) {
  const toastRef = useRef(onToastProp);
  toastRef.current = onToastProp;
  const onToast = useCallback((msg: string) => toastRef.current(msg), []);

  const [modules, setModules] = useState<CommunityModule[]>([]);
  const [videos, setVideos] = useState<CommunityVideo[]>([]);
  const [moduleId, setModuleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [videoModal, setVideoModal] = useState<{ editing: CommunityVideo | null } | null>(null);
  const [form, setForm] = useState<VideoInput>(emptyVideo(""));
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<CommunityVideo | null>(null);

  const [moduleModal, setModuleModal] = useState<{ editing: CommunityModule | null } | null>(null);
  const [moduleForm, setModuleForm] = useState({ title: "", description: "", icon: ICONS[0].value });

  const load = useCallback(async () => {
    let mods = await fetchModules();
    if (mods.length === 0) {
      await ensureDefaultModules().catch(() => {});
      mods = await fetchModules();
    }
    setModules(mods);
    setVideos(await fetchCommunityVideos());
  }, []);

  useEffect(() => {
    load()
      .catch(() => onToast("No se pudieron cargar los videos."))
      .finally(() => setLoading(false));
  }, [load, onToast]);

  const current = modules.find((m) => m.id === moduleId) ?? modules[0] ?? null;
  const list = useMemo(() => (current ? videos.filter((v) => v.moduleId === current.id) : []), [videos, current]);
  const countBy = useMemo(() => {
    const m: Record<string, number> = {};
    videos.forEach((v) => (m[v.moduleId] = (m[v.moduleId] ?? 0) + 1));
    return m;
  }, [videos]);

  // ---------- Módulos ----------
  const openModule = (m: CommunityModule | null) => {
    setFormError(null);
    setModuleForm(m ? { title: m.title, description: m.description, icon: m.icon } : { title: "", description: "", icon: ICONS[0].value });
    setModuleModal({ editing: m });
  };

  const submitModule = async () => {
    if (!moduleModal) return;
    const title = moduleForm.title.trim();
    if (title.length < 2) return setFormError("Escribe el nombre del módulo.");
    setSaving(true);
    setFormError(null);
    try {
      if (moduleModal.editing) await updateModule(moduleModal.editing.id, { title, description: moduleForm.description.trim(), icon: moduleForm.icon });
      else await createModule({ title, description: moduleForm.description.trim(), icon: moduleForm.icon }, modules.length ? Math.max(...modules.map((m) => m.sortOrder)) + 1 : 1);
      setModuleModal(null);
      await load();
      onToast(moduleModal.editing ? "Módulo actualizado" : "Módulo creado");
    } catch {
      setFormError("No se pudo guardar el módulo.");
    } finally {
      setSaving(false);
    }
  };

  const moveModule = async (m: CommunityModule, dir: -1 | 1) => {
    const i = modules.findIndex((x) => x.id === m.id);
    const other = modules[i + dir];
    if (!other) return;
    try {
      // Reasigna posiciones consecutivas para evitar empates.
      const next = [...modules];
      next[i] = other;
      next[i + dir] = m;
      await Promise.all(next.map((x, idx) => (x.sortOrder !== idx + 1 ? updateModule(x.id, { sortOrder: idx + 1 }) : Promise.resolve())));
      await load();
    } catch {
      onToast("No se pudo cambiar el orden.");
    }
  };

  const toggleModule = async (m: CommunityModule) => {
    try {
      await updateModule(m.id, { active: !m.active });
      await load();
    } catch {
      onToast("No se pudo cambiar el estado del módulo.");
    }
  };

  const removeModule = async (m: CommunityModule) => {
    const vids = videos.filter((v) => v.moduleId === m.id);
    if (!window.confirm(`¿Eliminar el módulo "${m.title}"?${vids.length ? ` Se borrarán sus ${vids.length} videos y sus archivos de Cloudflare.` : ""}`)) return;
    try {
      await deleteModule(m.id, vids);
      setModuleId(null);
      await load();
      onToast("Módulo eliminado");
    } catch {
      onToast("No se pudo eliminar el módulo.");
    }
  };

  // ---------- Videos ----------
  const openVideo = (v: CommunityVideo | null) => {
    if (!current) return;
    setFormError(null);
    setForm(
      v
        ? {
            moduleId: v.moduleId,
            title: v.title,
            description: v.description,
            videoUrl: v.videoUrl,
            coverLandscapeUrl: v.coverLandscapeUrl,
            coverPortraitUrl: v.coverPortraitUrl,
            layout: v.layout,
            featured: v.featured,
            durationSeconds: v.durationSeconds,
            published: v.published,
          }
        : emptyVideo(current.id)
    );
    setVideoModal({ editing: v });
  };

  const submitVideo = async () => {
    if (!videoModal) return;
    const input: VideoInput = { ...form, title: form.title.trim(), description: form.description.trim(), videoUrl: form.videoUrl.trim() };
    if (input.title.length < 2) return setFormError("Escribe un título para el video.");
    if (!/^https?:\/\//i.test(input.videoUrl)) return setFormError("Sube un archivo o pega un enlace que empiece con https://");
    setSaving(true);
    setFormError(null);
    try {
      const old = videoModal.editing;
      if (old) {
        await saveCommunityVideo(old.id, input);
        // Libera de Cloudflare los archivos que se reemplazaron.
        const pairs: [string, string][] = [
          [old.videoUrl, input.videoUrl],
          [old.coverLandscapeUrl, input.coverLandscapeUrl],
          [old.coverPortraitUrl, input.coverPortraitUrl],
        ];
        await Promise.all(pairs.filter(([a, b]) => a && a !== b && isOwnMedia(a)).map(([a]) => deleteMediaByUrl(a)));
      } else {
        const siblings = videos.filter((v) => v.moduleId === input.moduleId);
        await createCommunityVideo(input, siblings.length ? Math.max(...siblings.map((v) => v.sortOrder)) + 1 : 1);
      }
      setVideoModal(null);
      await load();
      onToast(old ? "Video actualizado" : "Video agregado");
    } catch {
      setFormError("No se pudo guardar el video. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const move = async (v: CommunityVideo, dir: -1 | 1) => {
    const i = list.findIndex((x) => x.id === v.id);
    const other = list[i + dir];
    if (!other) return;
    try {
      const next = [...list];
      next[i] = other;
      next[i + dir] = v;
      await Promise.all(next.map((x, idx) => (x.sortOrder !== idx + 1 ? patchCommunityVideo(x.id, { sortOrder: idx + 1 }) : Promise.resolve())));
      await load();
    } catch {
      onToast("No se pudo cambiar el orden.");
    }
  };

  const patch = async (v: CommunityVideo, p: Partial<{ published: boolean; featured: boolean }>) => {
    try {
      await patchCommunityVideo(v.id, p);
      await load();
    } catch {
      onToast("No se pudo guardar el cambio.");
    }
  };

  const remove = async (v: CommunityVideo) => {
    if (!window.confirm(`¿Eliminar "${v.title}"?${isOwnMedia(v.videoUrl) ? " También se borra el archivo de Cloudflare." : ""}`)) return;
    try {
      await deleteCommunityVideo(v);
      await load();
      onToast("Video eliminado");
    } catch {
      onToast("No se pudo eliminar el video.");
    }
  };

  const layoutBtn = (l: VideoLayout, label: string, sub: string) => (
    <button
      type="button"
      onClick={() => setForm({ ...form, layout: l })}
      className={`flex-1 rounded-xl border px-3 py-2 text-left cursor-pointer ${form.layout === l ? "border-primary-500 bg-primary-500/10" : "border-background-300 bg-background-100"}`}
    >
      <div className="text-xs font-semibold text-foreground-900">{label}</div>
      <div className="text-[10px] text-foreground-500">{sub}</div>
    </button>
  );

  return (
    <section>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-foreground-950">Videos de la comunidad por módulo</h2>
          <p className="text-xs text-foreground-500">Sube los videos y sus portadas desde aquí: quedan guardados en Cloudflare y aparecen en la Comunidad, bajo el módulo que elijas.</p>
        </div>
        <button onClick={() => openVideo(null)} disabled={!current} className="inline-flex items-center gap-1.5 bg-primary-500 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-50">
          <i className="ri-add-line"></i> Agregar video
        </button>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto pb-1 items-center" role="tablist" aria-label="Módulos">
        {modules.map((m) => (
          <button
            key={m.id}
            role="tab"
            aria-selected={current?.id === m.id}
            onClick={() => setModuleId(m.id)}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-semibold border cursor-pointer ${current?.id === m.id ? "bg-primary-500 text-white border-primary-500" : "bg-background-100 text-foreground-700 border-background-200 hover:border-primary-300"} ${m.active ? "" : "opacity-60"}`}
          >
            <i className={m.icon}></i> {m.title}
            <span className={`px-1.5 rounded-full text-[10px] ${current?.id === m.id ? "bg-white/25" : "bg-background-200"}`}>{countBy[m.id] ?? 0}</span>
          </button>
        ))}
        <button onClick={() => openModule(null)} className="inline-flex items-center gap-1 whitespace-nowrap px-3.5 py-2 rounded-full text-xs font-semibold border border-dashed border-primary-400 text-primary-700 hover:bg-primary-500/10 cursor-pointer">
          <i className="ri-add-line"></i> Nuevo módulo
        </button>
      </div>

      {current ? (
        <div className="mt-2 flex items-center gap-1 text-xs text-foreground-500 flex-wrap">
          <span className="mr-1">Módulo "{current.title}":</span>
          <button onClick={() => openModule(current)} className="px-2 py-1 rounded-lg hover:bg-background-200 cursor-pointer"><i className="ri-edit-line"></i> Editar</button>
          <button onClick={() => moveModule(current, -1)} disabled={modules[0]?.id === current.id} className="px-2 py-1 rounded-lg hover:bg-background-200 disabled:opacity-30 cursor-pointer"><i className="ri-arrow-left-s-line"></i> Mover</button>
          <button onClick={() => moveModule(current, 1)} disabled={modules[modules.length - 1]?.id === current.id} className="px-2 py-1 rounded-lg hover:bg-background-200 disabled:opacity-30 cursor-pointer">Mover <i className="ri-arrow-right-s-line"></i></button>
          <button onClick={() => toggleModule(current)} className="px-2 py-1 rounded-lg hover:bg-background-200 cursor-pointer"><i className={current.active ? "ri-eye-line" : "ri-eye-off-line"}></i> {current.active ? "Visible" : "Oculto"}</button>
          <button onClick={() => removeModule(current)} className="px-2 py-1 rounded-lg hover:bg-accent-500/10 text-accent-700 cursor-pointer"><i className="ri-delete-bin-line"></i> Eliminar</button>
        </div>
      ) : null}

      <div className="mt-3 flex flex-col gap-2.5">
        {loading ? (
          <div className="text-center text-foreground-500 py-8"><i className="ri-loader-4-line animate-spin text-2xl"></i></div>
        ) : list.length === 0 ? (
          <div className="bg-background-100 border border-dashed border-background-300 rounded-2xl p-8 text-center text-sm text-foreground-500">
            Aún no hay videos en <strong>{current?.title}</strong>. Pulsa "Agregar video" para subir el primero.
          </div>
        ) : (
          list.map((v, i) => (
            <div key={v.id} className="bg-background-100 border border-background-200 rounded-2xl p-3 flex items-center gap-3">
              <button onClick={() => setPreview(v)} aria-label="Ver video" className="relative w-24 flex-shrink-0 rounded-lg overflow-hidden bg-gradient-to-br from-primary-600/90 to-accent-600/80 cursor-pointer" style={{ aspectRatio: "16 / 9" }}>
                {v.coverLandscapeUrl || v.coverPortraitUrl ? <img src={v.coverLandscapeUrl || v.coverPortraitUrl} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
                <span className="absolute inset-0 flex items-center justify-center text-white"><i className="ri-play-fill text-2xl drop-shadow"></i></span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-foreground-950 truncate">{v.title}</div>
                <div className="text-[11px] text-foreground-500 flex flex-wrap gap-x-3">
                  <span>{isOwnMedia(v.videoUrl) ? "Guardado en Cloudflare" : "Enlace externo"}</span>
                  {v.durationSeconds ? <span>{fmtDuration(v.durationSeconds)}</span> : null}
                  <span>{v.layout === "portrait" ? "Vertical 4:5" : "Horizontal 16:9"}</span>
                  {v.featured ? <span className="text-amber-700">Destacado</span> : null}
                  <span className={v.published ? "text-primary-700" : "text-amber-700"}>{v.published ? "Publicado" : "Oculto"}</span>
                </div>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <button onClick={() => move(v, -1)} disabled={i === 0} aria-label="Subir" className="w-8 h-8 rounded-lg hover:bg-background-200 disabled:opacity-30 cursor-pointer"><i className="ri-arrow-up-s-line"></i></button>
                <button onClick={() => move(v, 1)} disabled={i === list.length - 1} aria-label="Bajar" className="w-8 h-8 rounded-lg hover:bg-background-200 disabled:opacity-30 cursor-pointer"><i className="ri-arrow-down-s-line"></i></button>
                <button onClick={() => patch(v, { featured: !v.featured })} aria-label="Destacar" title="Destacar" className="w-8 h-8 rounded-lg hover:bg-background-200 cursor-pointer"><i className={v.featured ? "ri-star-fill text-amber-500" : "ri-star-line"}></i></button>
                <button onClick={() => patch(v, { published: !v.published })} aria-label={v.published ? "Ocultar" : "Publicar"} title={v.published ? "Ocultar" : "Publicar"} className="w-8 h-8 rounded-lg hover:bg-background-200 cursor-pointer"><i className={v.published ? "ri-eye-line" : "ri-eye-off-line"}></i></button>
                <button onClick={() => openVideo(v)} aria-label="Editar" className="w-8 h-8 rounded-lg hover:bg-background-200 cursor-pointer"><i className="ri-edit-line"></i></button>
                <button onClick={() => remove(v)} aria-label="Eliminar" className="w-8 h-8 rounded-lg hover:bg-accent-500/10 text-accent-700 cursor-pointer"><i className="ri-delete-bin-line"></i></button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ---- Modal de video ---- */}
      {videoModal ? (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setVideoModal(null)}>
          <div className="bg-background-50 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="font-heading font-bold text-foreground-950">{videoModal.editing ? "Configurar video" : "Agregar video"}</h3>
            <div className="mt-4 grid gap-6 md:grid-cols-[1fr_280px]">
              <div className="flex flex-col gap-3 min-w-0">
                <label className="block text-xs font-semibold text-foreground-600">
                  Módulo
                  <select value={form.moduleId} onChange={(e) => setForm({ ...form, moduleId: e.target.value })} className={`${inputCls} mt-1`}>
                    {modules.map((m) => (
                      <option key={m.id} value={m.id}>{m.title}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-semibold text-foreground-600">
                  Título
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={120} className={`${inputCls} mt-1`} />
                </label>
                <label className="block text-xs font-semibold text-foreground-600">
                  Descripción corta (opcional)
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={140} rows={2} className={`${inputCls} mt-1 resize-none`} />
                </label>
                <div>
                  <div className="text-xs font-semibold text-foreground-600 mb-1">Video</div>
                  <VideoUploader
                    kind="community"
                    onUploaded={(r) => setForm((f) => ({ ...f, videoUrl: r.url, durationSeconds: r.durationSeconds ?? f.durationSeconds, title: f.title || r.fileName.replace(/\.[^.]+$/, "") }))}
                  />
                  <label className="block text-xs text-foreground-500 mt-2">
                    O pega un enlace (YouTube, Vimeo directo o .mp4)
                    <input value={form.videoUrl} onChange={(e) => setForm({ ...form, videoUrl: e.target.value })} placeholder="https://…" className={`${inputCls} mt-1`} />
                  </label>
                  {form.videoUrl ? (
                    <p className="mt-1 text-[11px] text-primary-700"><i className="ri-checkbox-circle-fill"></i> {isOwnMedia(form.videoUrl) ? "Video guardado en Cloudflare." : "Enlace listo."}{form.durationSeconds ? ` Duración: ${fmtDuration(form.durationSeconds)}.` : ""}</p>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <ImageUploader label="Portada horizontal" hint="16:9 · ej. 1280×720" ratio="16 / 9" value={form.coverLandscapeUrl} onChange={(u) => setForm((f) => ({ ...f, coverLandscapeUrl: u }))} />
                  <ImageUploader label="Portada vertical" hint="4:5 · ej. 1080×1350" ratio="4 / 5" value={form.coverPortraitUrl} onChange={(u) => setForm((f) => ({ ...f, coverPortraitUrl: u }))} />
                </div>
                <div>
                  <div className="text-xs font-semibold text-foreground-600 mb-1">Cómo se muestra en la comunidad</div>
                  <div className="flex gap-2">
                    {layoutBtn("landscape", "Horizontal 16:9", "Usa la portada horizontal")}
                    {layoutBtn("portrait", "Vertical 4:5", "Usa la portada vertical")}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                    <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publicado
                  </label>
                  <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Destacado (aparece primero)
                  </label>
                </div>
                {formError ? <p className="text-xs text-accent-700">{formError}</p> : null}
              </div>

              {/* Vista previa: la misma tarjeta que verá el socio */}
              <div className="md:sticky md:top-0 self-start">
                <div className="text-xs font-semibold text-foreground-600 mb-2">Así lo verá el socio</div>
                <div className="rounded-2xl bg-background-200/60 p-3">
                  <div className={form.layout === "portrait" ? "max-w-[190px] mx-auto" : ""}>
                    <VideoCard video={form} />
                  </div>
                </div>
                {!form.coverLandscapeUrl && !form.coverPortraitUrl ? (
                  <p className="mt-2 text-[11px] text-foreground-500">Sin portada se muestra un fondo de color con el botón de reproducir.</p>
                ) : form.layout === "landscape" && !form.coverLandscapeUrl ? (
                  <p className="mt-2 text-[11px] text-amber-700">No hay portada horizontal: se recorta la vertical. Sube una para que se vea bien.</p>
                ) : form.layout === "portrait" && !form.coverPortraitUrl ? (
                  <p className="mt-2 text-[11px] text-amber-700">No hay portada vertical: se recorta la horizontal. Sube una para que se vea bien.</p>
                ) : null}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setVideoModal(null)} disabled={saving} className="px-4 py-2 rounded-full text-sm font-semibold border border-background-300 cursor-pointer">Cancelar</button>
              <button onClick={submitVideo} disabled={saving} className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 cursor-pointer disabled:opacity-60">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---- Modal de módulo ---- */}
      {moduleModal ? (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !saving && setModuleModal(null)}>
          <div className="bg-background-50 rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="font-heading font-bold text-foreground-950">{moduleModal.editing ? "Editar módulo" : "Nuevo módulo"}</h3>
            <div className="mt-4 flex flex-col gap-3">
              <label className="block text-xs font-semibold text-foreground-600">
                Nombre
                <input value={moduleForm.title} onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })} maxLength={60} className={`${inputCls} mt-1`} placeholder="Ej. Ventas y cierre" />
              </label>
              <label className="block text-xs font-semibold text-foreground-600">
                Descripción (opcional)
                <input value={moduleForm.description} onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })} maxLength={140} className={`${inputCls} mt-1`} />
              </label>
              <div>
                <div className="text-xs font-semibold text-foreground-600 mb-1">Ícono</div>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map((o) => (
                    <button key={o.value} type="button" title={o.label} onClick={() => setModuleForm({ ...moduleForm, icon: o.value })} className={`w-10 h-10 rounded-xl border flex items-center justify-center cursor-pointer ${moduleForm.icon === o.value ? "border-primary-500 bg-primary-500/10 text-primary-700" : "border-background-300 text-foreground-600"}`}>
                      <i className={`${o.value} text-lg`}></i>
                    </button>
                  ))}
                </div>
              </div>
              {formError ? <p className="text-xs text-accent-700">{formError}</p> : null}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModuleModal(null)} disabled={saving} className="px-4 py-2 rounded-full text-sm font-semibold border border-background-300 cursor-pointer">Cancelar</button>
              <button onClick={submitModule} disabled={saving} className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 cursor-pointer disabled:opacity-60">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {preview ? <VideoPlayerModal url={preview.videoUrl} title={preview.title} onClose={() => setPreview(null)} /> : null}
    </section>
  );
}
